import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bookmark, CheckCircle2, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TutorialCard } from "@/components/TutorialCard";
import { PracticeLog } from "@/components/PracticeLog";
import { WeeklySummary } from "@/components/WeeklySummary";
import { AssignedSection } from "@/components/AssignedSection";
import { StreakBadges } from "@/components/StreakBadges";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";

const Stat = ({ icon: Icon, label, value, tone, testId }) => (
  <div className="rounded-3xl bg-white border border-slate-200 p-6 flex items-center gap-4" data-testid={testId}>
    <span className={`w-12 h-12 rounded-2xl grid place-items-center ${tone}`}><Icon className="w-6 h-6" /></span>
    <div><p className="font-display text-3xl font-extrabold text-slate-900">{value}</p><p className="text-sm text-slate-500">{label}</p></div>
  </div>
);

const Grid = ({ items, onBookmark, empty, testId }) =>
  items.length === 0 ? (
    <div className="rounded-3xl border border-dashed border-slate-300 p-12 text-center text-slate-500" data-testid={`${testId}-empty`}>
      {empty} <Link to="/" className="text-indigo-600 font-semibold hover:underline">Browse the library</Link>
    </div>
  ) : (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid={testId}>
      {items.map((p, i) => <TutorialCard key={p.tutorial_id} tutorial={p.tutorial} progress={p} onBookmark={onBookmark} index={i} />)}
    </div>
  );

export default function MyLearning() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [practice, setPractice] = useState(null);

  useEffect(() => {
    api.get("/progress").then((r) => setItems(r.data)).catch((e) => toast.error(errorMessage(e)));
  }, []);

  const onBookmark = async (t) => {
    try {
      const r = await api.post(`/progress/${t.id}/bookmark`);
      setItems((list) => list.map((p) => (p.tutorial_id === t.id ? { ...p, ...r.data, tutorial: p.tutorial } : p)));
      toast.success(r.data.is_bookmarked ? "Added to My Learning" : "Bookmark removed");
    } catch (e) { toast.error(errorMessage(e)); }
  };

  if (!items) return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12"><Skeleton className="h-64 rounded-3xl" /></div>;

  const inProgress = items.filter((p) => p.status === "in_progress");
  const done = items.filter((p) => p.status === "completed");
  const saved = items.filter((p) => p.is_bookmarked);
  const resume = inProgress[0];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-16" data-testid="my-learning-page">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12 items-end">
        <div className="lg:col-span-7 space-y-3">
          <p className="eyebrow">My Learning</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">Welcome back, {user?.name?.split(" ")[0] || "there"}</h1>
          <p className="text-base md:text-lg text-slate-600">Everything you have started, finished or saved — only you can see this.</p>
        </div>
        {resume && (
          <div className="lg:col-span-5 rounded-3xl bg-slate-900 text-white p-6 sm:p-8 relative overflow-hidden grain" data-testid="resume-card">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-300">Pick up where you left off</p>
            <p className="font-display text-xl sm:text-2xl font-bold mt-2 leading-snug">{resume.tutorial.title}</p>
            <p className="text-sm text-slate-300 mt-1">Step {Math.min((resume.last_step_index || 0) + 1, resume.tutorial.step_count)} of {resume.tutorial.step_count}</p>
            <Button asChild className="mt-5 rounded-full bg-white text-slate-900 hover:bg-indigo-100 hover:text-slate-900"><Link to={`/tutorials/${resume.tutorial_id}`} data-testid="resume-button"><PlayCircle className="w-4 h-4" /> Resume</Link></Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-6">
        <Stat icon={PlayCircle} label="In progress" value={inProgress.length} tone="bg-indigo-50 text-indigo-600" testId="stat-in-progress" />
        <Stat icon={CheckCircle2} label="Completed" value={done.length} tone="bg-emerald-50 text-emerald-600" testId="stat-completed" />
        <Stat icon={Bookmark} label="Bookmarked" value={saved.length} tone="bg-amber-50 text-amber-600" testId="stat-bookmarked" />
      </div>

      {practice?.badges && (
        <div className="rounded-3xl bg-white border border-slate-200 p-6 mb-12" data-testid="dashboard-badges">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="eyebrow">Milestones</p>
              <h2 className="font-display text-xl font-bold text-slate-900 mt-1">Streak badges</h2>
            </div>
            <p className="text-sm text-slate-500" data-testid="dashboard-streak">{practice.stats.current_streak || 0}-day streak</p>
          </div>
          <StreakBadges badges={practice.badges} />
        </div>
      )}

      <AssignedSection />

      <div className="mb-12 space-y-6">
        <WeeklySummary week={practice?.week} />
        <PracticeLog onData={setPractice} />
      </div>

      <Tabs defaultValue="in_progress">
        <TabsList className="rounded-full h-11 p-1 bg-slate-100 mb-8">
          <TabsTrigger value="in_progress" className="rounded-full px-5" data-testid="tab-in-progress">In progress</TabsTrigger>
          <TabsTrigger value="completed" className="rounded-full px-5" data-testid="tab-completed">Completed</TabsTrigger>
          <TabsTrigger value="bookmarked" className="rounded-full px-5" data-testid="tab-bookmarked">Bookmarked</TabsTrigger>
        </TabsList>
        <TabsContent value="in_progress"><Grid items={inProgress} onBookmark={onBookmark} empty="Nothing in progress yet." testId="in-progress-grid" /></TabsContent>
        <TabsContent value="completed"><Grid items={done} onBookmark={onBookmark} empty="No completed tutorials yet." testId="completed-grid" /></TabsContent>
        <TabsContent value="bookmarked"><Grid items={saved} onBookmark={onBookmark} empty="No bookmarks yet." testId="bookmarked-grid" /></TabsContent>
      </Tabs>
    </div>
  );
}
